import React, { useState, useEffect } from "react";
import LeadService from "../Lead/Controller/LeadController";
import Draggable from "react-draggable";

const LIMIT = 20;

const InboxModal = ({ onSave, onClose }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [allEmails, setAllEmails] = useState([]);
  const [displayEmails, setDisplayEmails] = useState([]);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // ✅ Fetch Folders
  const fetchFolders = async () => {
    try {
      const res = await LeadService.getMailFolders();
      if (res?.success) {
        setFolders(res.folders || []);

        // Select Inbox by default
        const inbox = res.folders.find(f => f.displayName === "Inbox");
        if (inbox) {
          fetchEmails(inbox.id);
          setSelectedFolder(inbox.id);
        }
      }
    } catch (err) {
      console.error("Folder fetch error:", err);
    }
  };

  // ✅ Fetch Emails by Folder
  const fetchEmails = async (folderId) => {
    setLoading(true);
    try {
      const res = await LeadService.inbox({
        folder: folderId,
        limit: 200
      });

      if (res?.success) {
        setAllEmails(res.emails || []);
        setDisplayEmails(res.emails.slice(0, LIMIT));
        setHasMore(res.emails.length > LIMIT);
        setPage(1);
        setExpandedEmailId(null);
        setSelectedEmails([]);
      }
    } catch (err) {
      console.error("Inbox fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const loadMore = () => {
    const nextPage = page + 1;
    const start = (nextPage - 1) * LIMIT;
    const end = nextPage * LIMIT;

    setDisplayEmails(prev => [
      ...prev,
      ...allEmails.slice(start, end)
    ]);

    setPage(nextPage);
    setHasMore(allEmails.length > end);
  };

  const toggleSelectEmail = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const toggleExpand = (idx) => {
    setExpandedEmailId(expandedEmailId === idx ? null : idx);
  };

  const handleSave = () => {
    if (!selectedEmails.length) {
      alert("Please select at least one email.");
      return;
    }
    onSave?.(selectedEmails);
  };

  const stripHtml = (html = "") =>
    html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

  return (
    <div className="modal fade leadDetailModal show d-block" style={{ zIndex: 1050 }}>
      <div className="modal-dialog modal-xl">
        <Draggable handle=".modal-header">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Mailbox</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            {!isMinimized && (
              <div className="modal-body d-flex p-0" style={{ height: "500px" }}>
                
                {/* ✅ LEFT SIDE – FOLDERS */}
                <div
                  style={{
                    width: "250px",
                    borderRight: "1px solid #ddd",
                    overflowY: "auto"
                  }}
                >
                  <ul className="list-group list-group-flush">
                    {folders.map(folder => (
                      <li
                        key={folder.id}
                        className={`list-group-item ${selectedFolder === folder.id ? "active" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedFolder(folder.id);
                          fetchEmails(folder.id);
                        }}
                      >
                        {folder.displayName}
                        {folder.unreadItemCount > 0 && (
                          <span className="badge ms-2" style={{color:"#333"}}>
                            {folder.unreadItemCount}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* ✅ RIGHT SIDE – EMAIL LIST */}
                <div style={{ flex: 1, overflowY: "auto", padding: "15px" }}>
                  {loading ? (
                    <p className="text-center text-muted">Loading...</p>
                  ) : displayEmails.length === 0 ? (
                    <p className="text-center text-muted">No emails found</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {displayEmails.map((email, idx) => (
                        <li key={email.uid} className="list-group-item p-0 border-0">
                          <div
                            className="d-flex align-items-start email-row"
                            onClick={() => toggleExpand(idx)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedEmails.includes(email)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelectEmail(email)}
                            />
                            <div className="ms-3 flex-grow-1" style={{marginLeft:"4px"}}>
                              <strong>{email.subject}</strong>
                              <div className="text-muted small">
                                {email.from}
                              </div>
                              {email.text && (
                                <div className="small">
                                  {stripHtml(email.text).slice(0, 80)}
                                </div>
                              )}
                            </div>
                            <div className="small text-muted">
                              {email.date && new Date(email.date).toLocaleDateString()}
                            </div>
                          </div>

                          {expandedEmailId === idx && (
                            <div className="mt-2 p-3 bg-light border rounded">
                              <p><strong>From:</strong> {email.from}</p>
                              <p><strong>To:</strong> {email.to}</p>
                              <p><strong>Subject:</strong> {email.subject}</p>
                              <hr />
                              {email.bodyType === "html" ? (
                                <div
                                  dangerouslySetInnerHTML={{ __html: email.text }}
                                />
                              ) : (
                                <p style={{ whiteSpace: "pre-wrap" }}>
                                  {email.text}
                                </p>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                Save Selected
              </button>
              {hasMore && !loading && (
                <button className="btn btn-outline-secondary" onClick={loadMore}>
                  Load More
                </button>
              )}
            </div>
          </div>
        </Draggable>
      </div>
    </div>
  );
};

export default InboxModal;
